import { useEffect, useRef, useState, useCallback } from "react";
import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { LoadingOutlined, SearchOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useDiscountClients } from "../../../../../hooks/useDiscounts";
import type { NewBillDiscountIn } from "../../../../../api/bills";
import {
  useApplyBillDiscount,
  useRemoveBillDiscount,
  useBills,
} from "../../../../../hooks/useBills";
import { useViewport } from "../../../../../hooks/useViewport";
import { formatSignedBillPercent } from "../../billDiscount";
import { Center, ClientRow } from "./ClientRow";
import { ClientFilterChips, type ClientFilter } from "./ClientFilterChips";
import { ClientsFooter } from "./ClientsFooter";
import { billTotalAtClientPercent } from "./clientDiscountPreview";
import { ModalHeader } from "../../modalPrimitives";

interface ClientsModalProps {
  open: boolean;
  onClose: () => void;
  billId: number | null;
  // Режим „нова маса" (billId=null + onPendingSelect): изборът НЕ вика BE —
  // връща се на родителя и пътува с CreateBillIn при „Поръчай", а BE закача
  // клиента при откриването на сметката.
  pendingDiscount?: NewBillDiscountIn | null;
  onPendingSelect?: (discount: NewBillDiscountIn | null) => void;
}


export const ClientsModal = ({
  open,
  onClose,
  billId,
  pendingDiscount = null,
  onPendingSelect,
}: ClientsModalProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const {
    data: discountData,
    isLoading,
    error: discountError,
  } = useDiscountClients();
  const clients = discountData?.items ?? [];
  const { data: bills = [] } = useBills({ fromCacheOnly: true });
  const applyDiscountMutation = useApplyBillDiscount();
  const removeDiscountMutation = useRemoveBillDiscount();
  const selectedBill = bills.find((b) => b.id === billId);
  const pendingMode = !billId && !!onPendingSelect;
  // „Има ли клиент" = активен запис в discount_applied (клиент с 0% също
  // е клиент; discount_percent е null без клиент). В pending режим
  // „приложен" е чакащият избор (още няма сметка).
  const hasDiscount = pendingMode
    ? !!pendingDiscount
    : !!selectedBill?.discount_applied;

  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedRemove, setSelectedRemove] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<ClientFilter>("all");
  const [applying, setApplying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedClientId(null);
      setSelectedRemove(false);
      setSearchTerm("");
      setFilter("all");
    }
  }, [open]);

  const now = new Date();
  const currentHHMMSS = now.toTimeString().slice(0, 8);
  const jsDay = now.getDay();
  const currentWeekday = jsDay === 0 ? 6 : jsDay - 1;

  const eligibleClients = clients.filter((client) => {
    if (!client.is_active) return false;
    if (client.active_from && client.active_to) {
      if (
        currentHHMMSS < client.active_from ||
        currentHHMMSS > client.active_to
      )
        return false;
    }
    if (client.weekdays && client.weekdays.length > 0) {
      if (!client.weekdays.includes(currentWeekday)) return false;
    }
    return true;
  });

  const q = searchTerm.trim().toLowerCase();
  const searchedClients = eligibleClients.filter(
    (client) =>
      client.name.toLowerCase().includes(q) ||
      (client.card_number ?? "").toLowerCase().includes(q),
  );
  // Кутийките под търсачката — броят е сред намерените от търсенето.
  const matchesFilter = (client: (typeof eligibleClients)[number], key: ClientFilter) =>
    key === "all" ||
    (key === "discount" && client.kind !== "surcharge" && client.percent > 0) ||
    (key === "surcharge" && client.kind === "surcharge") ||
    (key === "onAccount" && client.can_pay_on_account);
  const countOf = (key: ClientFilter) =>
    searchedClients.filter((client) => matchesFilter(client, key)).length;
  const hasSurcharges = eligibleClients.some((client) => client.kind === "surcharge");
  const filterOptions: { key: ClientFilter; label: string; count: number }[] = [
    { key: "all", label: t("clients.filterAll"), count: countOf("all") },
    { key: "discount", label: t("clients.filterDiscount"), count: countOf("discount") },
    ...(hasSurcharges
      ? [{ key: "surcharge" as const, label: t("clients.filterSurcharge"), count: countOf("surcharge") }]
      : []),
    { key: "onAccount", label: t("clients.onAccountBadge"), count: countOf("onAccount") },
  ];
  const filteredClients = searchedClients.filter((client) => matchesFilter(client, filter));

  // По подразбиране нищо не е избрано; при смяна на търсенето или на
  // кутийката селекцията отпада (иначе избран клиент може да остане скрит).
  useEffect(() => {
    setSelectedClientId(null);
  }, [searchTerm, filter]);

  // Държим маркирания ред видим при клавиатурна навигация.
  useEffect(() => {
    if (selectedClientId == null) return;
    listRef.current
      ?.querySelector(`[data-client-id="${selectedClientId}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedClientId]);

  const applyDiscount = useCallback(
    async (clientId: number | null, remove: boolean) => {
      if (applying) return;
      if (!clientId && !remove) return;
      // Режим „нова маса" — селекцията само се връща на родителя (без BE);
      // съобщението за успех идва чак след „Поръчай" (реалното прилагане).
      if (pendingMode) {
        if (remove) {
          onPendingSelect?.(null);
        } else if (clientId) {
          const client = eligibleClients.find((c) => c.id === clientId);
          if (!client) return;
          onPendingSelect?.({
            clientId: client.id,
            // надбавката пътува като отрицателен процент (както в apply)
            percent:
              client.kind === "surcharge" ? -client.percent : client.percent,
            clientName: client.name,
            discountType: client.discount_type,
          });
        }
        onClose();
        return;
      }
      if (!billId) return;
      setApplying(true);
      try {
        if (remove) {
          // съобщението зависи от вида на приложената корекция (знака)
          const appliedPercent = Number(
            selectedBill?.discount_applied?.percent ??
              selectedBill?.discount_percent ??
              0,
          );
          await removeDiscountMutation.mutateAsync(billId);
          message.success(
            t(
              appliedPercent < 0
                ? "clients.removeSurchargeSuccess"
                : "clients.removeSuccess",
            ),
          );
        } else if (clientId) {
          const client = eligibleClients.find((c) => c.id === clientId);
          if (!client) return;
          await applyDiscountMutation.mutateAsync({
            billId,
            data: {
              billId,
              clientId: client.id,
              // надбавката пътува като отрицателен процент — BE формулата
              // (100 − p)/100 тогава увеличава цената
              percent:
                client.kind === "surcharge" ? -client.percent : client.percent,
              clientName: client.name,
              discountType: client.discount_type,
            },
          });
          message.success(
            t(
              client.kind === "surcharge"
                ? "clients.applySurchargeSuccess"
                : "clients.applySuccess",
            ),
          );
        }
        onClose();
      } catch (err: unknown) {
        message.error(getApiErrorMessage(err, t("common.error")));
      } finally {
        setApplying(false);
      }
    },
    [
      billId,
      applying,
      pendingMode,
      onPendingSelect,
      eligibleClients,
      selectedBill,
      applyDiscountMutation,
      removeDiscountMutation,
      onClose,
      message,
      t,
    ],
  );

  const handleApply = useCallback(
    () => applyDiscount(selectedClientId, selectedRemove),
    [applyDiscount, selectedClientId, selectedRemove],
  );

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const inInput = e.target instanceof HTMLInputElement;

      if (e.key === "Escape") {
        if (inInput) {
          setSearchTerm("");
          (e.target as HTMLElement)?.blur?.();
          return;
        }
        e.preventDefault();
        onClose();
        return;
      }

      // Стрелки — работят и докато пишем в търсачката: местят селекцията
      // (от „нищо избрано" отиват на първия ред), останалото я движи.
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (filteredClients.length === 0) return;
        e.preventDefault();
        setSelectedRemove(false);
        setSelectedClientId((prev) => {
          const idx = filteredClients.findIndex((c) => c.id === prev);
          const next =
            e.key === "ArrowDown"
              ? idx < 0
                ? 0
                : Math.min(idx + 1, filteredClients.length - 1)
              : idx < 0
                ? 0
                : Math.max(idx - 1, 0);
          return filteredClients[next].id;
        });
        return;
      }

      // Enter (едно натискане) — прилага избрания клиент/„премахни";
      // ако няма нищо избрано, прилага директно първия резултат.
      if (e.key === "Enter") {
        e.preventDefault();
        if (selectedRemove) {
          applyDiscount(null, true);
        } else {
          const clientId = selectedClientId ?? filteredClients[0]?.id ?? null;
          if (clientId) applyDiscount(clientId, false);
        }
        return;
      }

      if (inInput) return;
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setSearchTerm((prev) => prev + e.key);
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [
    open,
    applyDiscount,
    onClose,
    isDesktop,
    filteredClients,
    selectedClientId,
    selectedRemove,
  ]);

  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPadClass = isNarrow ? "px-4" : "px-6";

  // Приложеният процент по сметката (или чакащият избор при „нова маса").
  const appliedPercent = pendingMode
    ? (pendingDiscount?.percent ?? 0)
    : (selectedBill?.discount_applied?.percent ??
      selectedBill?.discount_percent ??
      0);

  // Етикетът на реда „Премахни …" — същите текстове както досега.
  const removeLabel = (() => {
    if (pendingMode && pendingDiscount) {
      return t(
        pendingDiscount.percent < 0
          ? "clients.removeSurchargeNamed"
          : "clients.removeDiscountNamed",
        {
          name: pendingDiscount.clientName,
          discount: formatSignedBillPercent(pendingDiscount.percent),
        },
      );
    }
    const isSurcharge = appliedPercent < 0;
    const amount = Math.abs(
      parseFloat(selectedBill?.total_discount ?? "0"),
    ).toFixed(2);
    const name = selectedBill?.discount_applied?.client_name;
    if (name)
      return t(
        isSurcharge
          ? "clients.removeSurchargeNamed"
          : "clients.removeDiscountNamed",
        { name, discount: amount },
      );
    return t(
      isSurcharge ? "clients.removeSurcharge" : "clients.removeDiscount",
      { discount: amount },
    );
  })();

  // Обобщението във футъра: сметката сега → с избрания клиент (или без
  // приложения при „Премахни"). При „нова маса" сметка още няма — без него.
  const selectedClient = filteredClients.find((client) => client.id === selectedClientId);
  const selectedPercent = selectedClient
    ? selectedClient.kind === "surcharge"
      ? -selectedClient.percent
      : selectedClient.percent
    : null;
  const summary = (() => {
    if (pendingMode || !selectedBill) return null;
    const targetPercent = selectedRemove ? 0 : selectedPercent;
    if (targetPercent === null) return null;
    const beforeCents = Math.round(parseFloat(selectedBill.total_amount) * 100);
    const afterCents = Math.round(billTotalAtClientPercent(selectedBill, targetPercent) * 100);
    const label = selectedRemove
      ? t("clients.summaryWithout", { name: selectedBill.name })
      : targetPercent < 0
        ? t("clients.summaryAfterSurcharge", { name: selectedBill.name })
        : t("clients.summaryAfterDiscount", { name: selectedBill.name });
    return { label, beforeCents, afterCents };
  })();
  const primaryLabel = selectedRemove
    ? t("clients.removeButton")
    : selectedPercent !== null
      ? t("clients.applyPercent", { percent: formatSignedBillPercent(selectedPercent) })
      : t("clients.select");

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<TeamOutlined />}
          title={t("clients.title")}
          subtitle={t("clients.total", { count: filteredClients.length })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          className={`${bodyPadClass} flex min-h-0 flex-1 flex-col gap-3 pt-4 pb-3`}
        >
          {/* Търсачка — същата кутия като при смяна на сервитьор */}
          <label className="flex h-11 shrink-0 items-center gap-3 rounded-[10px] border border-pos-line3 bg-pos-card px-4">
            <SearchOutlined className="text-[17px] text-pos-dim" />
            <input
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("clients.searchPlaceholder")}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-semibold text-pos-ink outline-none placeholder:text-pos-muted md:text-sm"
            />
          </label>

          <ClientFilterChips options={filterOptions} value={filter} onChange={setFilter} />

          <div
            ref={listRef}
            className="min-h-[120px] max-h-[50vh] flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {isLoading ? (
              <Center>
                <LoadingOutlined spin className="text-[36px] text-pos-accent" />
              </Center>
            ) : discountError ? (
              <Center tone="danger">{String(discountError)}</Center>
            ) : filteredClients.length === 0 ? (
              <Center>{t("clients.noClients")}</Center>
            ) : (
              <div className="flex flex-col gap-1.5">
                {hasDiscount && (
                  <ClientRow
                    tone="danger"
                    selected={selectedRemove}
                    label={removeLabel}
                    percentLabel={formatSignedBillPercent(appliedPercent)}
                    onClick={() => {
                      setSelectedRemove(true);
                      setSelectedClientId(null);
                    }}
                  />
                )}
                {filteredClients.map((client) => (
                  <ClientRow
                    key={client.id}
                    clientId={client.id}
                    selected={selectedClientId === client.id}
                    label={client.name}
                    onAccount={client.can_pay_on_account}
                    percentLabel={formatSignedBillPercent(
                      client.kind === "surcharge"
                        ? -client.percent
                        : client.percent,
                    )}
                    onClick={() => {
                      if (client.is_active) {
                        setSelectedClientId(client.id);
                        setSelectedRemove(false);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <ClientsFooter
          summary={summary}
          primaryLabel={primaryLabel}
          canApply={!!selectedClientId || selectedRemove}
          applying={applying}
          onBack={onClose}
          onApply={handleApply}
          isNarrow={isNarrow}
          isDesktop={isDesktop}
        />
      </div>
    </Modal>
  );
};
